/**
 * Keeps the activity-log export list current while a job is still running.
 *
 * Polls a status fingerprint and reloads only once something actually changed. It does
 * NOT reload on a timer: each reload is a fresh page with fresh Alpine state, so an
 * attempt counter would reset on every tick and the bound below would never bind — an
 * abandoned tab with one stuck row would poll for as long as it stayed open.
 *
 * The bound matters because "poll while a row is non-terminal" has no exit for a job
 * killed hard enough to skip ProcessExportJob::failed() (OOM, SIGKILL): that row sits at
 * "processing" permanently.
 */
const INTERVAL_MS = 5000
const MAX_ATTEMPTS = 60 // ~5 minutes, then the user refreshes if they still care

document.addEventListener('alpine:init', () => {
    window.Alpine.data('activityLogExportPoll', (hasRunning = false) => ({
        attempts: 0,
        navigating: false,
        timer: null,

        init() {
            if (!hasRunning) {
                return
            }

            this.timer = setInterval(() => this.check(), INTERVAL_MS)

            // An interval outlives the component that started it. The flag matters as much
            // as the clearInterval: stopping the timer cannot recall a request already in
            // flight, and a check() that resolves *during* a navigation reloads this page
            // on top of the one the user asked for — their click is silently undone and
            // they land back here. pagehide covers the bfcache path beforeunload misses.
            const leaving = () => {
                this.navigating = true
                this.stop()
            }

            window.addEventListener('beforeunload', leaving)
            window.addEventListener('pagehide', leaving)
        },

        stop() {
            if (this.timer !== null) {
                clearInterval(this.timer)
                this.timer = null
            }
        },

        async check() {
            if (++this.attempts > MAX_ATTEMPTS) {
                this.stop()

                return
            }

            let fresh
            try {
                const response = await fetch(this.statusUrl(), {
                    headers: {Accept: 'application/json'},
                })

                // A 403/419/500 here means the session expired or the app is down.
                // Reloading on it would bounce the user to a login page mid-poll;
                // stopping leaves the list as it was.
                if (!response.ok) {
                    this.stop()

                    return
                }

                fresh = (await response.json()).statuses
            } catch {
                this.stop()

                return
            }

            // Re-checked after the await, not just before it: the navigation that makes a
            // reload harmful usually starts while this fetch is still outstanding.
            if (this.navigating) {
                return
            }

            if (this.hasChanged(fresh)) {
                this.stop()
                window.location.reload()
            }
        },

        statusUrl() {
            return `${window.location.pathname.replace(/\/+$/, '')}/status`
        },

        /**
         * Per-id, never whole-object. The endpoint answers with the newest 25 exports
         * while the page may be showing an older page of them, so comparing the two
         * objects wholesale would differ on every tick from page 2 onward and reload
         * in a loop. Ids the response does not carry are simply not evidence either way.
         */
        hasChanged(fresh) {
            let rendered
            try {
                rendered = JSON.parse(this.$el.dataset.exportStatuses || '{}')
            } catch {
                return false
            }

            return Object.entries(rendered)
                .some(([id, status]) => fresh[id] !== undefined && fresh[id] !== status)
        },
    }))
})
